import axios from "axios";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from "react-query";
import { FINDINGS, IMPORT_GENPT_TESTS, SIEMBAGPT_SESSIONS, SIEMBAGPT_FINDINGS, SIEMBAGPT_SESSION_DETAILS, SIEMBAGPT_SESSION_INSIGHTS, SIEMBAGPT_USER_SESSION_INSIGHTS, GENPT_TEST_KEY_INSIGHT, CVE_IDs, SAVE_CVE_IDs } from "./utils/constants";
import { paginationAttr } from "../actions/commonActions";
import { useRefreshCTEMDashboard } from "./dashboard";
import { useValidateLogin } from "./auth";
import Storage from "../utils/Storage";

const resolveCompanyId = (companyIdOption, currentCompanyId) => {
  if (companyIdOption) return companyIdOption;
  try {
    const company = JSON.parse(Storage.decryptStorageData('company_id'));
    if (company?.id) return company.id;
  } catch {
    // ignore parse errors
  }
  return currentCompanyId ?? null;
};

export const useGenPTSessions = (options = {}) => {
  const {
    search = null,
    pageSize = 10,
    sortBy = [],
    enabled = true,
    userId = null,
    tags,
    status, 
    startDate, 
    endDate,
    getInsights,
    batchId,
    notificationStartDateTime,
    notificationEndDateTime,
    getUnique,
    ...queryOptions
  } = options
  return useInfiniteQuery([SIEMBAGPT_SESSIONS, {
    search, 
    pageSize, 
    sortBy, 
    userId,
    tags,
    status, 
    startDate, 
    endDate,
    getInsights,
    batchId,
    notificationStartDateTime,
    notificationEndDateTime,
    getUnique
  }], async ({ pageParam = 1 }) => {
      let res = await axios.get(`/genpt/user/sessions${paginationAttr({ 
        page: pageParam, 
        pageSize, 
        search, 
        sortBy,  
        tags,
        status, 
        startDate, 
        endDate,
        getInsights,
        batchId,
        notificationStartDateTime,
        notificationEndDateTime,
        getUnique
      })}`);
      return res.data
  },{
    getNextPageParam: lastPage => lastPage?.nextPage ?? undefined,
    enabled,
    keepPreviousData: true,
    ...queryOptions
  })
};

export const useDeleteGenPTSession = () => {
  const queryClient = useQueryClient();
  return useMutation(async (sessionId) => {
    let res = await axios.delete(`/genpt/sessions/${sessionId}`)
    return res.data
}, {
  onSuccess: async () => {
      await queryClient.invalidateQueries(SIEMBAGPT_SESSIONS);
      await queryClient.invalidateQueries(SIEMBAGPT_USER_SESSION_INSIGHTS);
  }
});
}

export const useGenPTSessionDetails = (options = {}) => {
  const {
    sessionId = '',
    checkEnriched = false,
    ...queryOptions
} = options
  return useQuery([SIEMBAGPT_SESSION_DETAILS, sessionId, checkEnriched], async () => {
    let res = await axios.get(`/genpt/sessions/${sessionId}${checkEnriched ? '?checkEnriched=true' : ''}`);
    return res.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const useGenPTSessionInsights = (options = {}) => {
  const {
    sessionId = '',
    ...queryOptions
} = options
  return useQuery([SIEMBAGPT_SESSION_INSIGHTS, sessionId], async () => {
    let res = await axios.get(`/genpt/sessions/${sessionId}/insights`);
    return res.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const useGenptKeyInsightsDetailsById = (options={}) => {
  const {
    sortBy = [],
    sessionId = null,
    insightId = null,
  } = options
  return useQuery([GENPT_TEST_KEY_INSIGHT, {
    sortBy,
    sessionId,
    insightId,
  }], async () => {
    let attr = "";
    let sortValue = "";
    sortBy?.forEach((item, index) => {
        let sortKey = item.id?.replaceAll('_', '');
        let dir = item.desc ? "desc" : "asc";
        sortValue += `${index > 0 ? "," : ""}${sortKey}_${dir}`;
    });

    if (sortValue) attr += `?sort=${sortValue}`;

    let res = await axios.get(`/genpt/sessions/${sessionId}/insights/${insightId}${attr}`);
    return res?.data;
  }, {
      enabled: !!sessionId && !!insightId,
      keepPreviousData: true
  })
}

export const useFetchGenPTConfigurations = (options = {}) => {
  const {
    ...queryOptions
} = options
  return useQuery(['GENPT_CONFIGURATIONS'], async () => {
    let res = await axios.get(`/genpt/configurations`);
    return res.data
  }, {
    ...queryOptions
  })
};

export const useGenPTUserTestsInsights = (options = {}) => {
  const {
    userId = '',
    triaged_as = null,
    ...queryOptions
} = options
  return useQuery([SIEMBAGPT_USER_SESSION_INSIGHTS, userId, triaged_as], async () => {
    const triagedAsParam = triaged_as ? `?triaged_as=${encodeURIComponent(triaged_as)}` : '';
    let res = await axios.get(`/genpt/user/tests/insights${triagedAsParam}`);
    return res.data
  }, {
    enabled: !!userId,
    ...queryOptions
  })
};

export const useFetchSessionResponses = (options = {}) => {
  const {
    sessionId = '',
    ...queryOptions
} = options
  return useQuery(['Session Responses', sessionId], async () => {
    const res = await axios.get(`/genpt/sessions/${sessionId}/latestResponse`);
    return res.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const useFetchLatestSessionActivity = (options = {}) => {
  const {
    sessionId = '',
    activityId = '',
    ...queryOptions
} = options
  return useQuery(['Session Activities', sessionId, activityId], async () => {
    const response = await axios.get(`/genpt/sessions/${sessionId}/activity?activityId=${activityId}`);
    return response.data
  }, {
    enabled: !!sessionId,
    ...queryOptions
  })
};

export const fetchActivityScreenshots = async (sessionId, activityId) => {
  try {
    const response = await axios.get(`/genpt/sessions/${sessionId}/activity?activityId=${activityId}`);
    return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
}

export const useStartGenPTEngine = (options) => {
  return useMutation(async (data) => {
      // Extract nonce from metadata?.nonce if available
      const nonce = data?.metadata?.nonce || data?.nonce || null;
      const chat_id = data?.chat_id || null;
      
      // Prepare payload with nonce and chat_id
      const payload = {
        ...data,
        nonce,
        chat_id,
      };
      
      let res = await axios.post(`/genpt/test`, payload)
      return res.data
  }, options)
};

// IDOR stage mutations were folded into `useStartGenPTEngine` (above). Every IDOR stage rides the same
// `POST /genpt/test` endpoint, discriminated by `test_type:'idor'` + `idor_stage`; callers build the
// payload with `idorStagePayload(stage, data)` (see Chat/utils/idor.js) and pass it to
// `useStartGenPTEngine`. Stages: predeclaration | auth_retry | phase1 | object_access | declaration.

/**
 * POST /genpt/schedules — create a recurring / one-off GenPT schedule (no /genpt/test start).
 * Body should be snake_case (see `buildGenptScheduleCreateBody` in `buildGenptSchedulePayload.js`).
 */
export const useCreateGenPTSchedule = (options) => {
  return useMutation(async (data) => {
    const res = await axios.post(`/genpt/schedules`, data);
    return res.data;
  }, options);
};

/** PUT /genpt/schedules/:scheduleId — update an existing schedule (same body shape as create where applicable). */
/**
 * PUT /genpt/schedules/:scheduleId (partial body supported).
 * @param {object} options - useMutation options
 * @returns {import('react-query').UseMutationResult}
 * Mutation variables: `{ scheduleId: string|number, body }` — snake_case fields only for
 * changed values; `ends_type` is lowercase (`never` | `on_date` | `after_occurrences`);
 * `repeat_unit` is `day` | `week` | `month` | `year`. Auth / company_id stay on axios defaults.
 */
export const useUpdateGenPTSchedule = (options) => {
  return useMutation(async ({ scheduleId, body }) => {
    const res = await axios.put(`/genpt/schedules/${scheduleId}`, body);
    return res.data;
  }, options);
};

/**
 * PATCH /genpt/schedules/:scheduleId/status — pause or resume (body: { status: 'paused' | 'active' }).
 */
export const usePatchGenptScheduleStatus = (options) => {
  return useMutation(async ({ scheduleId, status }) => {
    const res = await axios.patch(`/genpt/schedules/${scheduleId}/status`, {
      status,
    });
    return res.data;
  }, options);
};

/** DELETE /genpt/schedules/:scheduleId */
export const useDeleteGenPTSchedule = (options) => {
  return useMutation(async ({ scheduleId }) => {
    const res = await axios.delete(`/genpt/schedules/${scheduleId}`);
    return res.data;
  }, options);
};

/**
 *
 * @param {string} debouncedTrimmedName - trimmed name after debounce
 * @param {object} [options]
 * @param {string} [options.originalName] - when editing, skip the request if the name is unchanged from server prefill
 * @param {boolean} [options.enabled=true] - set false in read-only / disabled contexts
 */
export const useGenptScheduleValidateName = (debouncedTrimmedName, options = {}) => {
  const { originalName = '', enabled: enabledOption = true, ...queryOptions } = options;
  const name = debouncedTrimmedName?.trim() || '';
  const baseline = String(originalName || '').trim();
  const unchanged = baseline !== '' && name === baseline;
  const enabled = Boolean(name) && !unchanged && enabledOption;

  return useQuery(
    ['GENPT_SCHEDULE_VALIDATE', 'name', name],
    async () => {
      const res = await axios.post('/genpt/schedules/validate', { check: 'name', name });
      return res.data;
    },
    {
      enabled,
      staleTime: 30 * 1000,
      ...queryOptions,
    }
  );
};

/**
 * GET /genpt/schedules — paginated list for All Schedules (optional filters; company from axios headers).
 * @param {object} options - { pageSize?, search?, assetId?, status?, repeat_unit?, platform_category?, latest_status?, nextRunAtFrom?, nextRunAtTo?, sortBy?, enabled?, ...queryOptions }
 */
export const useListGenptSchedules = (options = {}) => {
  const {
    pageSize = 20,
    search = null,
    assetId = null,
    status = null,
    repeat_unit = null,
    platform_category = null,
    latest_status = null,
    nextRunAtFrom = null,
    nextRunAtTo = null,
    sortBy = [],
    enabled = true,
    ...queryOptions
  } = options;

  return useInfiniteQuery(
    [
      'LIST_GENPT_SCHEDULES',
      {
        pageSize,
        search,
        assetId,
        status,
        repeat_unit,
        platform_category,
        latest_status,
        nextRunAtFrom,
        nextRunAtTo,
        sortBy,
      },
    ],
    async ({ pageParam = 1 }) => {
      const params = { page: pageParam, pageSize };
      if (search) params.search = search;
      if (assetId != null && String(assetId).length > 0) params.assetId = assetId;
      if (status != null && String(status).length > 0) params.status = status;
      if (repeat_unit != null && String(repeat_unit).length > 0) params.repeat_unit = repeat_unit;
      if (platform_category != null && String(platform_category).length > 0) {
        params.platform_category = platform_category;
      }
      if (latest_status != null && String(latest_status).length > 0) {
        params.latest_status = latest_status;
      }
      if (nextRunAtFrom != null && String(nextRunAtFrom).length > 0) {
        params.nextRunAtFrom = nextRunAtFrom;
      }
      if (nextRunAtTo != null && String(nextRunAtTo).length > 0) {
        params.nextRunAtTo = nextRunAtTo;
      }
      const res = await axios.get(`/genpt/schedules`, { params });
      return res.data;
    },
    {
      getNextPageParam: (lastPage) => lastPage?.nextPage ?? undefined,
      enabled: Boolean(enabled),
      keepPreviousData: true,
      ...queryOptions,
    }
  );
};

/** ROAR-4366: React Query key for company-wide *active-only* schedule total (headline on All Schedules). */
export const GENPT_SCHEDULES_ACTIVE_TOTAL_QUERY_KEY = 'GENPT_SCHEDULES_ACTIVE_TOTAL_COUNT';

/**
 * GET /genpt/schedules?page=1&pageSize=1&status=active — pagination `total` is active schedule count only.
 * The main list query often omits `status` (all rows); its `total` must not drive the “Active schedules” headline.
 */
export const useGenptSchedulesActiveTotalCount = (options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  return useQuery(
    [GENPT_SCHEDULES_ACTIVE_TOTAL_QUERY_KEY],
    async () => {
      const res = await axios.get(`/genpt/schedules`, {
        params: { page: 1, pageSize: 1, status: 'active' },
      });
      return res.data;
    },
    {
      enabled: Boolean(enabled),
      staleTime: 30 * 1000,
      ...queryOptions,
    }
  );
};

/**
 * GET /genpt/schedules/:scheduleId — single schedule (detail for preview drawer).
 */
export const useGenptScheduleById = (scheduleId, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  return useQuery(
    ['GENPT_SCHEDULE_BY_ID', scheduleId],
    async () => {
      const res = await axios.get(`/genpt/schedules/${scheduleId}`);
      return res.data;
    },
    {
      enabled: Boolean(
        enabled && scheduleId != null && String(scheduleId).length > 0
      ),
      ...queryOptions,
    }
  );
};

/**
 * GET /genpt/schedules/:scheduleId/history — execution rows, newest first.
 */
export const useGenptScheduleHistory = (scheduleId, options = {}) => {
  const { pageSize = 20, enabled = true, ...queryOptions } = options;
  return useInfiniteQuery(
    ['GENPT_SCHEDULE_HISTORY', scheduleId, pageSize],
    async ({ pageParam = 1 }) => {
      const res = await axios.get(`/genpt/schedules/${scheduleId}/history`, {
        params: { page: pageParam, pageSize },
      });
      return res.data;
    },
    {
      getNextPageParam: (lastPage) => {
        if (lastPage == null) return undefined;
        if (typeof lastPage.hasNextPage === 'boolean') {
          return lastPage.hasNextPage ? lastPage.nextPage ?? undefined : undefined;
        }
        return lastPage.nextPage ?? undefined;
      },
      enabled: Boolean(
        enabled && scheduleId != null && String(scheduleId).length > 0
      ),
      keepPreviousData: true,
      ...queryOptions,
    }
  );
};

/** Parse total schedule count from GET /genpt/schedules list responses (shape may vary by backend). */
export function getGenptSchedulesTotalCount(data) {
  if (data == null) return 0;
  if (typeof data.total === 'number') return data.total;
  if (typeof data.totalCount === 'number') return data.totalCount;
  if (typeof data.count === 'number') return data.count;
  if (Array.isArray(data)) return data.length;
  const list = data.data ?? data.schedules ?? data.items ?? data.rows ?? [];
  if (Array.isArray(list)) {
    const t =
      data.pagination?.total ??
      data.meta?.total ??
      data.page?.total ??
      (typeof data.pagination?.totalCount === 'number' ? data.pagination.totalCount : undefined);
    if (typeof t === 'number') return t;
    return list.length;
  }
  return 0;
}

/**
 * GET /genpt/schedules — filtered list (e.g. active schedules per asset).
 * @param {object} params - { assetId, page?, pageSize?, status?, repeat_unit? }
 * When `repeat_unit` is omitted, the request does not filter by recurrence (all repeat units).
 */
export const useGenptSchedulesList = (params, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  const assetId = params?.assetId;
  const page = params?.page ?? 1;
  const pageSize = params?.pageSize ?? 20;
  const status = params?.status ?? 'active';
  const repeat_unit =
    params && Object.prototype.hasOwnProperty.call(params, 'repeat_unit')
      ? params.repeat_unit
      : undefined;

  return useQuery(
    ['GENPT_SCHEDULES_LIST', assetId, page, pageSize, status, repeat_unit ?? '__any__'],
    async () => {
      const requestParams = {
        page,
        pageSize,
        assetId,
        status,
      };
      if (repeat_unit != null && repeat_unit !== '') {
        requestParams.repeat_unit = repeat_unit;
      }
      const res = await axios.get(`/genpt/schedules`, {
        params: requestParams,
      });
      return res.data;
    },
    {
      staleTime: 30 * 1000,
      ...queryOptions,
      enabled: Boolean(
        enabled &&
          assetId != null &&
          String(assetId).length > 0
      ),
    }
  );
};

/**
 * Same **POST** `/genpt/schedules/validate` as name checks; body uses `check: "slot"` — same-asset time/recurrence conflict
 * (replaces client-side paginated GET `/genpt/schedules` for ROAR-4341).
 * @param {{ assetId: string|number, schedule: object, excludeScheduleId?: string|number|null, signal?: AbortSignal }} params
 * @returns {Promise<{ success?: boolean, available?: boolean, check?: string }>}
 */
export async function validateGenptScheduleSlotRequest({
  assetId,
  schedule,
  excludeScheduleId,
  signal,
}) {
  const id = assetId != null && String(assetId).trim() !== '' ? String(assetId).trim() : null;
  if (!id || !schedule || typeof schedule !== 'object') {
    return { success: true, available: true, check: 'slot' };
  }
  const body = {
    check: 'slot',
    assetId: id,
    schedule,
  };
  const ex =
    excludeScheduleId != null && String(excludeScheduleId).trim() !== ''
      ? String(excludeScheduleId).trim()
      : null;
  if (ex) body.excludeScheduleId = ex;
  const res = await axios.post('/genpt/schedules/validate', body, { signal });
  return res.data ?? {};
}

export const useFetchAssetCredentials = (assetId, options = {}) => {
  const { enabled = true, ...queryOptions } = options;
  return useQuery(
    ['assetCredentials', assetId],
    async () => {
      if (!assetId) return null;
      const res = await axios.get(`/assets/credentials`, {
        params: { assetId },
      });
      return res.data;
    },
    {
      ...queryOptions,
      enabled: Boolean(enabled && !!assetId),
    }
  );
};

/** Parse credential id from POST /assets/credentials (response shape varies by API version). */
export const extractCreatedAssetCredentialId = (data) => {
  if (data == null) return null;
  const tryNode = (node) => {
    if (node == null) return null;
    if (typeof node === 'string' || typeof node === 'number') {
      const s = String(node).trim();
      return s || null;
    }
    if (typeof node === 'object') {
      const id = node.id ?? node.credential_id ?? node.credentialId;
      if (id != null && String(id).trim()) return String(id).trim();
    }
    return null;
  };
  return (
    tryNode(data) ??
    tryNode(data?.data) ??
    tryNode(data?.credential) ??
    tryNode(data?.data?.credential) ??
    null
  );
};

/**
 * Persist a new GenPT login credential for an asset (used when UI "Add New Credential" is chosen).
 * Pairs with GET /assets/credentials; backend expects `name` for the credential label.
 */
export const createAssetCredential = async (payload) => {
  const { assetId, name, username, password, mfaSecretKey, loginUrl } = payload || {};
  const res = await axios.post(`/assets/credentials`, {
    assetId,
    name,
    username,
    password,
    ...(mfaSecretKey ? { mfaSecretKey } : {}),
    ...(loginUrl ? { loginUrl } : {}),
  });
  return res.data;
};

export const useCreateAssetCredential = (options = {}) => {
  const queryClient = useQueryClient();
  const { onSuccess: userOnSuccess, ...rest } = options;
  return useMutation(
    async (body) => createAssetCredential(body),
    {
      onSuccess: async (data, variables, context) => {
        if (variables?.assetId) {
          await queryClient.invalidateQueries(['assetCredentials', variables.assetId]);
        }
        await userOnSuccess?.(data, variables, context);
      },
      ...rest,
    }
  );
};

export const usePutExternalTicket = (findingId) => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.put(`/genpt/findings/external_ticket/${findingId}`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      await queryClient.invalidateQueries(FINDINGS);
    }
  })
};

/** PUT /genpt/findings/:findingId — update a GenPT finding (e.g. tag, status). */
export const useUpdateFinding = (options = {}) => {
  const queryClient = useQueryClient();
  const { data: userData } = useValidateLogin();
  const { onSuccess: userOnSuccess, companyId: companyIdOption, ...rest } = options;
  const companyId = resolveCompanyId(companyIdOption, userData?.user?.currentCompanyId);
  const refreshCTEMDashboard = useRefreshCTEMDashboard(companyId);
  return useMutation(
    async ({ findingId, ...body }) => {
      const res = await axios.put(`/genpt/findings/${findingId}`, body);
      return res.data;
    },
    {
      onSuccess: async (data, variables, context) => {
        await queryClient.invalidateQueries(FINDINGS);
        // await queryClient.invalidateQueries(SIEMBAGPT_FINDINGS);
        // await refreshGenPTOverviewCache();
        // if (companyId) {
        //   await refreshCTEMDashboard.mutateAsync({ companyId });
        // }
        await userOnSuccess?.(data, variables, context);
      },
      ...rest,
    }
  );
};

export const useValidateTarget = (options) => {
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/validate_target`, data)
      return res.data
  }, options)
};

export const useValidateAPICollection = (options) => {
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/validate_api_collection`, data)
      return res.data
  }, options)
};

export const useGenPTTestSteps = (options) => {
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/test/steps`, data)
      return res.data
  }, options)
};

export const useEnrichFindings = (sessionId, findingId, options) => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/sessions/${sessionId}/findings/${findingId}/enrichment`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      await queryClient.invalidateQueries(FINDINGS);
    },
    ...options
  })
};

export const useSaveSessionProgress = (sessionId) => {
  return useMutation(async (data) => {
      let res = await axios.put(`/genpt/sessions/${sessionId}/save`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      // await queryClient.invalidateQueries(FINDINGS);
    }
  })
};

export const getSessionDetails = async (sessionId) => {
  try {
  const response = await axios.get(`/genpt/sessions/${sessionId}`);
  return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
};

export const fetchSessionFindings = async (sessionId) => {
  try{
    let res = await axios.get(`/genpt/sessions/${sessionId}/findings`);
    return res.data
  }catch (err){
    console.error('Error scanning:', err);
  }
};

export const useFetchAllFindings = (options = {}) => {
  const {
    search = null,
    pageSize = 10,
    sortBy = [],
    enabled = true,
    status = null,
    severity = null,
    sessionId = null,
    cve = [],
    startDate = "",
    endDate = "",
    triage_marked_as = null,
    expires_on = null,
    refetchInterval = false,
  } = options
  return useInfiniteQuery([SIEMBAGPT_FINDINGS, {
    search,
    pageSize,
    sortBy,
    status,
    severity,
    sessionId,
    cve,
    startDate,
    endDate,
    triage_marked_as,
    expires_on,
}], async ({ pageParam = 1 }) => {
      let res = await axios.get(`/genpt/findings${paginationAttr({ page: pageParam, pageSize, search, sortBy, status, severity, sessionId, cve, startDate, endDate, triage_marked_as, expires_on })}`);
      return res?.data
  },{
    getNextPageParam: lastPage => lastPage?.nextPage ?? undefined,
    enabled,
    keepPreviousData: true,
    refetchInterval
  })
}

export const fetchSessionFindingDetails = async (sessionId,findingId) => {
  try{
    let res = await axios.get(`/genpt/sessions/${sessionId}/findings/${findingId}`);
    return res.data
  }catch (err){
    console.error('Error scanning:', err);
  }
};

export const getSessionResponses = async (sessionId) => {
  try {
  const response = await axios.get(`/genpt/sessions/${sessionId}/latestResponse`);
  return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
};

export const useImportGenptTests = (options) => {
  const queryClient = useQueryClient();
  return useMutation(
      async (data) => {

          let res = await axios.post(`/genpt/sessions/import`, data, {
              headers: {
                  'Content-Type': 'multipart/form-data', // Set the content type as multipart/form-data
              },
          });
          return res.data;
      },
      options,
      {
          onSuccess: async () => {
              await queryClient.invalidateQueries(IMPORT_GENPT_TESTS);
          },
      }
  );
};

export const useStartBulkTest = () => {
  const queryClient = useQueryClient()
  return useMutation(async (data) => {
      let res = await axios.post(`/genpt/bulk_test`, data)
      return res.data
  }, { 
    onSuccess: async () => {
      await queryClient.invalidateQueries(SIEMBAGPT_SESSIONS);
      await queryClient.invalidateQueries(SIEMBAGPT_USER_SESSION_INSIGHTS);
    }
  })
};

export const useCVEIDs = (options = {}) => {
  return useQuery(
    CVE_IDs,
    async () => {
      let res = await axios.get(`/genpt/cve_ids`);
      return res?.data?.data;
    },
    {
      ...options,
    }
  );
};

export const useSaveCVEIDs = (options = {}) => {
  return useQuery(
    SAVE_CVE_IDs,
    async () => {
      let res = await axios.get(`/genpt/save_cve_ids`);
      return res?.data?.data;
    },
    {
      ...options,
    }
  );
};

export const refreshGenPTOverviewCache = async () => {
  try {
  const response = await axios.post(`/genpt/refresh_genpt_overview`);
  return response.data
  } catch (error) {
    console.error('Error scanning:', error);
  }
};
